import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/** サーバー側 generateEmbeddingFn の timeoutSeconds(300) に合わせる */
const EMBEDDING_CALL_TIMEOUT_MS = 300_000;
const DEFAULT_CONCURRENCY = 4;

export type EmbeddingProgress = {
  done: number;
  total: number;
  failed: number;
};

/**
 * 取り込んだページの検索用データ（embedding）を作る。
 * generateEmbeddingFn は、知識軸が ON のページなら公開RAG用 embeddings にも自動で反映する。
 * 少数並列で実行し、1 件失敗したら 1 回だけやり直す。失敗した boardId を返す。
 */
export async function generateEmbeddingsForBoards(
  workspaceId: string,
  boardIds: string[],
  onProgress?: (p: EmbeddingProgress) => void,
  concurrency: number = DEFAULT_CONCURRENCY,
): Promise<{ failedBoardIds: string[] }> {
  const total = boardIds.length;
  const failedBoardIds: string[] = [];
  let done = 0;
  let next = 0;
  onProgress?.({ done, total, failed: 0 });
  if (total === 0) return { failedBoardIds };

  const call = httpsCallable(functions, 'generateEmbeddingFn', {
    timeout: EMBEDDING_CALL_TIMEOUT_MS,
  });

  const runOne = async (boardId: string): Promise<boolean> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await call({ workspaceId, boardId });
        return true;
      } catch {
        // 1 回だけやり直す
      }
    }
    return false;
  };

  const worker = async () => {
    while (true) {
      const i = next++;
      if (i >= total) return;
      const ok = await runOne(boardIds[i]);
      if (!ok) failedBoardIds.push(boardIds[i]);
      done++;
      onProgress?.({ done, total, failed: failedBoardIds.length });
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));
  return { failedBoardIds };
}
